import { Container, Graphics } from "pixi.js";
import { COLORS, KICK, TEAM_IDENTITY, type Ball, type Player } from "@golazo/shared";
import { lerp } from "../core/math.js";

interface TrailPoint {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  width: number;
  color: number;
}

const MAX_POINTS = 96;
const BALL_SPEED_MIN = 340;

/** Estelas de movimiento para dash y tiros potentes. Solo render. */
export interface TrailsLayer {
  container: Container;
  /** Ráfaga corta al hacer dash. */
  burstDash(x: number, y: number, fx: number, fy: number, color: number): void;
  sync(
    alpha: number,
    ball: Ball,
    players: readonly Player[],
    ownerId: string | null,
    renderDt: number,
  ): void;
  update(dt: number): void;
}

export function createTrailsLayer(): TrailsLayer {
  const container = new Container();
  container.label = "trails";

  const gfx = new Graphics();
  container.addChild(gfx);

  const points: TrailPoint[] = [];
  let write = 0;

  const spawn = (p: Omit<TrailPoint, "maxLife">): void => {
    if (write >= MAX_POINTS) write = 0;
    const slot = points[write];
    const entry = { ...p, maxLife: p.life };
    if (slot) Object.assign(slot, entry);
    else points[write] = entry;
    write += 1;
  };

  return {
    container,

    burstDash(x: number, y: number, fx: number, fy: number, color: number): void {
      const len = Math.hypot(fx, fy) || 1;
      const nx = fx / len;
      const ny = fy / len;
      for (let i = 0; i < 8; i++) {
        const back = i * 4;
        spawn({
          x: x - nx * back,
          y: y - ny * back,
          life: 0.22 + i * 0.015,
          width: 5 - i * 0.35,
          color,
        });
      }
    },

    sync(alpha, ball, players, ownerId, renderDt): void {
      const bx = lerp(ball.prevPos.x, ball.pos.x, alpha);
      const by = lerp(ball.prevPos.y, ball.pos.y, alpha);
      const speed = Math.hypot(ball.vel.x, ball.vel.y);

      if (ownerId === null && speed >= BALL_SPEED_MIN) {
        const shoot = speed >= KICK.SHOOT_SPEED * KICK.MIN_POWER * 0.75;
        const t = Math.min(1, (speed - BALL_SPEED_MIN) / 900);
        spawn({
          x: bx - ball.vel.x * renderDt * 0.04,
          y: by - ball.vel.y * renderDt * 0.04,
          life: shoot ? 0.32 + t * 0.18 : 0.2 + t * 0.12,
          width: 2.5 + t * (shoot ? 5 : 2.5),
          color: shoot ? 0xffe58a : COLORS.BALL,
        });
      }

      for (const p of players) {
        if (p.burstTime <= 0) continue;
        const px = lerp(p.prevPos.x, p.pos.x, alpha);
        const py = lerp(p.prevPos.y, p.pos.y, alpha);
        const color = TEAM_IDENTITY[p.side].kit.accent;
        spawn({
          x: px - p.facing.x * p.radius * 0.4,
          y: py - p.facing.y * p.radius * 0.4,
          life: 0.18,
          width: 3.2,
          color,
        });
      }
    },

    update(dt: number): void {
      gfx.clear();
      let alive = 0;
      const live: TrailPoint[] = [];

      for (const p of points) {
        if (!p || p.life <= 0) continue;
        p.life -= dt;
        if (p.life <= 0) continue;
        live.push(p);
      }

      if (live.length < 2) {
        container.visible = live.length > 0;
        if (live.length === 1) {
          const p = live[0]!;
          const a = (p.life / p.maxLife) ** 2;
          gfx.circle(p.x, p.y, p.width * 0.5).fill({ color: p.color, alpha: a * 0.5 });
        }
        return;
      }

      for (let i = 0; i < live.length - 1; i++) {
        const a = live[i]!;
        const b = live[i + 1]!;
        const ta = (a.life / a.maxLife) ** 2;
        const tb = (b.life / b.maxLife) ** 2;
        const alpha = (ta + tb) * 0.5 * 0.55;
        if (alpha < 0.02) continue;
        gfx.moveTo(a.x, a.y)
          .lineTo(b.x, b.y)
          .stroke({
            width: (a.width + b.width) * 0.5,
            color: a.color,
            alpha,
            cap: "round",
            join: "round",
          });
        alive += 1;
      }

      container.visible = alive > 0 || live.length > 0;
    },
  };
}
